import { Typography } from '@mui/material';
import { athleteDisplayName } from '@strength/domain';
import { I18nTranslator } from '@strength/i18n';
import { DateTime } from 'luxon';
import { formatDate } from '@strength/utils';
import { getLocale } from '../../../../utils/i18n';
import { trpc } from '../../../../../trpc/react';

export type ProvenanceLinesProps = {
  /** profileId uuid or the 'AMS' sentinel. */
  enteredBy?: string;
  /** ISO timestamp of when the value was entered. */
  enteredOn?: string;
  /** Rendered between the date and the Entered By/On lines (e.g. "Latest Source: …"). */
  sourceLine?: { label: string; value: string } | null;
  /** Hide the standalone date line — redundant when Entered On is shown. */
  hideDate?: boolean;
  /** Explicit date (YYYY-MM-DD) for the standalone line — used by day headers
   * whose prescription may carry no enteredOn. Falls back to enteredOn. */
  headerDate?: string;
  label?: string | null;
  /** i18n keys for the by/on labels — defaults to enteredBy/enteredOn. */
  labelKeys?: {
    by: 'enteredBy' | 'lastUpdatedBy';
    on: 'enteredOn' | 'lastUpdatedOn';
  };
  t: I18nTranslator;
};

/**
 * Resolves an enteredBy profileId to a display name. Renders the raw id as a
 * fallback while loading or when the lookup fails (e.g. a deactivated
 * profile), so the hover never blocks on the fetch.
 */
const ProfileName = ({ profileId }: { profileId: string }) => {
  const { data: profile } = trpc.roster.athleteProfile.useQuery(
    { profileId },
    { staleTime: 60_000, retry: false },
  );

  return <>{profile ? athleteDisplayName(profile) : profileId}</>;
};

/**
 * Shared "Entered By / Entered On" lines for actuals and planned hovers.
 * Lines are omitted individually when the field is absent, so rows written
 * before provenance existed show nothing.
 */
export const ProvenanceTooltipLines = ({
  enteredBy,
  enteredOn,
  sourceLine,
  hideDate = false,
  headerDate,
  label,
  labelKeys = { by: 'enteredBy', on: 'enteredOn' },
  t,
}: ProvenanceLinesProps) => {
  // enteredOn is a full ISO timestamp; formatDate expects YYYY-MM-DD.
  const toFormatted = (iso?: string | null) =>
    iso
      ? formatDate(iso, getLocale(), {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        })
      : undefined;
  // Standalone date line: explicit headerDate (day headers) or the enteredOn date.
  const formattedDate =
    toFormatted(headerDate ?? undefined) ??
    toFormatted(
      enteredOn ? DateTime.fromISO(enteredOn).toISODate() : undefined,
    );
  // Entered On always reflects the actual stamp, never the header fallback.
  const formattedEnteredOn = toFormatted(
    enteredOn ? DateTime.fromISO(enteredOn).toISODate() : undefined,
  );

  // Plain date first, then Entered By, then Entered On. Stacked so callers
  // that pass this straight into a tooltip title still get one line per row.
  // Labels are grey, values white.
  return (
    <div className="flex flex-col items-start gap-0.5">
      {!hideDate && formattedDate && (
        <Typography color="white" variant="caption" className="font-semibold">
          {formattedDate}
        </Typography>
      )}
      {sourceLine && (
        <Typography color="grey" variant="caption">
          {sourceLine.label}:{' '}
          <Typography color="white" variant="caption" component="span">
            {sourceLine.value}
          </Typography>
        </Typography>
      )}
      {enteredBy && (
        <Typography color="grey" variant="caption">
          {t(labelKeys.by)}:{' '}
          <Typography color="white" variant="caption" component="span">
            {enteredBy === 'AMS' ? (
              'AMS'
            ) : (
              <ProfileName profileId={enteredBy} />
            )}
          </Typography>
        </Typography>
      )}
      {formattedEnteredOn && (
        <Typography color="grey" variant="caption">
          {t(labelKeys.on)}:{' '}
          <Typography color="white" variant="caption" component="span">
            {formattedEnteredOn}
          </Typography>
        </Typography>
      )}
      {label && (
        <Typography color="grey" variant="caption">
          {label}
        </Typography>
      )}
    </div>
  );
};
